import React, { memo } from 'react';
import InfoTooltip from '../common/InfoTooltip';
import { PerformanceHero, PerformanceWindow } from '../../types/performance';
import { fmtUsd, fmtPct, fmtShare, fmtRatio, fmtUsdShort } from '../../utils/performanceFormat';

interface PerformanceHeroKPIsProps {
  hero: PerformanceHero | null;
  window: PerformanceWindow | null;
  loading: boolean;
}

type Tone = 'gain' | 'loss' | 'warn' | 'neutral';

// Colour is meaning only: signed values read gain/loss, zero/unknown read neutral.
const toneOf = (v: number | null | undefined): Tone => (v == null || v === 0 ? 'neutral' : v > 0 ? 'gain' : 'loss');

/**
 * The four headline cards, every figure from gold.portfolio_period_kpis
 * (glossary premium_income / cost_of_capped_upside / income_yield /
 * option_overlay_return / roaic / stock_return / portfolio_return /
 * max_drawdown / portfolio_value). Formatting only.
 */
const PerformanceHeroKPIs: React.FC<PerformanceHeroKPIsProps> = ({ hero, window: w, loading }) => {
  if (loading) {
    return (
      <div className="perf-kpis" aria-busy="true">
        {[1, 2, 3, 4].map((i) => <div key={i} className="perf-kpi is-skel" />)}
      </div>
    );
  }
  if (!hero || !w) {
    return <div className="perf-kpi-empty">No performance data for this period</div>;
  }
  const yr = w.annualised ? ' /yr' : '';

  const cards: {
    label: string;
    value: string;
    color: Tone;
    tooltip?: string;
    sub1?: { label: string; value: string; color?: Tone };
    sub2?: { label: string; value: string; color?: Tone };
  }[] = [
    {
      label: 'Covered call income',
      value: fmtUsd(hero.premium_income),
      color: toneOf(hero.premium_income),
      tooltip: 'Premium income — the time value you keep as your options decay (the strategy\'s purpose). The cost of capped upside is the gain handed back when stocks rise past their strikes; the two add up to Option P&L.\n\nIncome yield: premium income ÷ average capital committed, per year. Always read next to the option overlay return.',
      sub1: { label: 'Capped upside', value: fmtUsd(hero.cost_of_capped_upside), color: toneOf(hero.cost_of_capped_upside) },
      sub2: { label: 'Income yield', value: `${fmtPct(hero.income_yield, 1)}/yr`, color: toneOf(hero.income_yield) },
    },
    {
      label: 'Return on capital',
      value: fmtPct(hero.roaic),
      color: toneOf(hero.roaic),
      tooltip: `Total P&L (stock + options + dividends) ÷ the average capital committed over the period${w.annualised ? ', per year (a year or longer)' : ' (not annualised under a year)'}.\n\nOption overlay: option P&L ÷ the same capital. Profit factor: money made on winners ÷ money lost on losers.`,
      sub1: { label: 'Option overlay', value: `${fmtPct(hero.option_overlay_return)}${yr}`, color: toneOf(hero.option_overlay_return) },
      sub2: { label: 'Profit factor', value: fmtRatio(hero.profit_factor), color: hero.profit_factor == null ? 'neutral' : hero.profit_factor >= 1.5 ? 'gain' : hero.profit_factor >= 1 ? 'warn' : 'loss' },
    },
    {
      label: 'Portfolio return',
      value: `${fmtPct(hero.return_twr)}${yr}`,
      color: toneOf(hero.return_twr),
      tooltip: 'Time-weighted return on market value (adding or withdrawing capital does not distort it), dividends included, compared with the S&P 500\'s total return over exactly the same sessions.\n\nStock return: the shares alone, as if no options had been sold. Max drawdown: the largest fall from a previous high.',
      sub1: { label: 'Stocks only', value: `${fmtPct(hero.stock_return)}${yr}`, color: toneOf(hero.stock_return) },
      sub2: { label: 'vs S&P 500', value: `${fmtPct(hero.alpha)}`, color: toneOf(hero.alpha) },
    },
    {
      label: 'Portfolio',
      value: hero.portfolio_value == null ? '—' : fmtUsdShort(hero.portfolio_value),
      color: 'neutral',
      tooltip: 'Market value at the last close of the period: shares at their price minus the options\' buy-back cost, plus the cash set aside for puts.\n\nMax drawdown: largest fall from a previous high in the period (negative).',
      sub1: { label: 'Max drawdown', value: fmtPct(hero.max_drawdown, 1), color: toneOf(hero.max_drawdown) },
      sub2: { label: 'Win rate', value: `${fmtShare(hero.win_rate)} · ${hero.trade_count ?? 0} trades` },
    },
  ];

  return (
    <div className="perf-kpis">
      {cards.map((card, i) => (
        <div key={i} className="perf-kpi">
          <div className="perf-kpi-l">
            {card.label}
            {card.tooltip && <InfoTooltip text={card.tooltip} />}
          </div>
          <div className={`perf-kpi-v is-${card.color}`}>{card.value}</div>
          <div className="perf-kpi-sub">
            {card.sub1 && (
              <div>
                {card.sub1.label} <b className={card.sub1.color ? `is-${card.sub1.color}` : undefined}>{card.sub1.value}</b>
              </div>
            )}
            {card.sub2 && (
              <div>
                {card.sub2.label} <b className={card.sub2.color ? `is-${card.sub2.color}` : undefined}>{card.sub2.value}</b>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default memo(PerformanceHeroKPIs);
